import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import './TaskSupplierSelect.css';

export interface TaskSupplierOption {
  id: string;
  name: string;
}

interface TaskSupplierSelectProps {
  options: TaskSupplierOption[];
  value: string[];
  derivedLabel?: string | null;
  onChange: (orgIds: string[]) => void;
  disabled?: boolean;
}

const TaskSupplierSelect: React.FC<TaskSupplierSelectProps> = ({
  options,
  value,
  derivedLabel,
  onChange,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [draft, setDraft] = useState<string[]>(value);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const selected = new Set(draft);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const placeMenu = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.max(rect.width, 200);
    const left = Math.min(rect.left, window.innerWidth - width - 8);
    setMenuPos({ top: rect.bottom + 4, left: Math.max(8, left), width });
  };

  useEffect(() => {
    if (!open) return;
    placeMenu();
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
      }
    };
    const onScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const label = options.filter((o) => selected.has(o.id)).map((o) => o.name).join(', ')
    || 'Not set';

  const commit = (next: string[]) => {
    setDraft(next);
    onChange(next);
  };

  const pick = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    commit(selected.has(id) ? [] : [id]);
    setOpen(false);
  };

  return (
    <div className={`pm-task-supplier${open ? ' pm-dropdown-host--open' : ''}`} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="pm-task-supplier-trigger"
        disabled={disabled || options.length === 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{options.length === 0 ? 'No suppliers on this deal' : label}</span>
        <ChevronDown size={12} aria-hidden />
      </button>
      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          className="pm-dropdown pm-task-supplier-menu"
          role="listbox"
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, width: menuPos.width, right: 'auto' }}
        >
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="option"
              aria-selected={selected.has(opt.id)}
              className={`pm-dropdown-item${selected.has(opt.id) ? ' is-active' : ''}`}
              onMouseDown={(e) => pick(e, opt.id)}
            >
              {opt.name}
            </button>
          ))}
          {draft.length > 0 && (
            <button
              type="button"
              className="pm-dropdown-item"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                commit([]);
                setOpen(false);
              }}
            >
              Clear
            </button>
          )}
        </div>,
        document.body,
      )}
      {derivedLabel && (
        <p className="pm-task-supplier-hint">From approved quote: {derivedLabel}</p>
      )}
    </div>
  );
};

export default TaskSupplierSelect;
